import { useState, useEffect, useCallback } from 'react';
import { poolApi, Pool, technicianApi, Technician, parameterApi, PoolParameter, inventoryApi, ChemicalInventory, ChemicalUsageRequest } from '../../services/poolService';
import { measurementApi, Reading, MeasurementCreateRequest, ReadingStatus } from '../../services/measurementService';

// Mapeo de IDs de formulario a IDs de parámetros del backend (camelCase)
const PARAM_ID_MAPPING: { [key: string]: string } = {
  'alkalinity': 'alkalinity',
  'ph': 'ph',
  'stabilizer': 'stabilizer',
  'freeChlorine': 'freeChlorine',
  'combinedChlorine': 'combinedChlorine',
  'hardness': 'hardness',
  'phosphates': 'phosphates',
  'salt': 'salt',                   // Cloro Producido por Sal (4-5 ppm)
  'salinity': 'salinity',           // Salinidad Total (2700-3400 ppm)
  'scuba_alkalinity': 'scuba_alkalinity',
  'scuba_bromine': 'scuba_bromine',
  'scuba_chlorine': 'scuba_chlorine',
  'scuba_copper': 'scuba_copper',
  'scuba_cya': 'scuba_cya',
  'scuba_hardness': 'scuba_hardness',
  'scuba_phosphate': 'scuba_phosphate',
  'scuba_oxygen': 'scuba_oxygen',
  'scuba_ph': 'scuba_ph'
};

interface FormData {
  // Selección de piscina
  poolId: string;
  // Parámetros estándar
  alkalinity: string;
  ph: string;
  stabilizer: string;
  freeChlorine: string;
  combinedChlorine: string;
  hardness: string;
  phosphates: string;
  salt: string;
  salinity: string;
  // Parámetros Scuba3s
  scuba_alkalinity: string;
  scuba_bromine: string;
  scuba_chlorine: string;
  scuba_copper: string;
  scuba_cya: string;
  scuba_hardness: string;
  scuba_phosphate: string;
  scuba_oxygen: string;
  scuba_ph: string;
  // Productos químicos: mapa dinámico inventoryId -> cantidad usada
  chemicalUsages: Record<number, string>;
  // Metadatos
  date: string;
  time: string;
  technician: string;
  notes: string;
  sampleImage?: string;
  measurementDevice: 'standard' | 'scuba3s' | 'both';
}

export default function RegistroDatos() {
  const [pools, setPools] = useState<Pool[]>([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [poolsError, setPoolsError] = useState<string>('');
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(true);
  const [techniciansError, setTechniciansError] = useState<string>('');
  const [parameters, setParameters] = useState<PoolParameter[]>([]);
  const [loadingParameters, setLoadingParameters] = useState(true);
  // Inventario real de la piscina seleccionada
  const [poolInventory, setPoolInventory] = useState<ChemicalInventory[]>([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [submittingUsage, setSubmittingUsage] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    poolId: '',
    alkalinity: '',
    ph: '',
    stabilizer: '',
    freeChlorine: '',
    combinedChlorine: '',
    hardness: '',
    phosphates: '',
    salt: '',
    salinity: '',
    scuba_alkalinity: '',
    scuba_bromine: '',
    scuba_chlorine: '',
    scuba_copper: '',
    scuba_cya: '',
    scuba_hardness: '',
    scuba_phosphate: '',
    scuba_oxygen: '',
    scuba_ph: '',
    chemicalUsages: {},
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    technician: '',
    notes: '',
    sampleImage: '',
    measurementDevice: 'both'
  });

  const [validationStatus, setValidationStatus] = useState<{[key: string]: 'optimal' | 'warning' | 'critical' | ''}>({});

  // Cargar las piscinas disponibles al montar el componente
  useEffect(() => {
    const loadPools = async () => {
      try {
        setLoadingPools(true);
        setPoolsError('');
        console.log('Cargando piscinas desde API...');
        const poolsData = await poolApi.getAll();
        console.log('Piscinas cargadas:', poolsData);
        setPools(poolsData.filter(pool => pool.active));
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        console.error('Error al cargar piscinas:', error);
        setPoolsError(`Error de conexión: ${errorMessage}`);

        // Usar datos mock si falla la API
        setPools([
          { id: 1, name: 'Piscina Principal', location: 'Área Central', capacity: '500L', type: 'COMMERCIAL', active: true, createdAt: '', updatedAt: '' },
          { id: 2, name: 'Piscina Infantil', location: 'Zona Niños', capacity: '100L', type: 'RESIDENTIAL', active: true, createdAt: '', updatedAt: '' },
          { id: 3, name: 'Piscina Terapéutica', location: 'Centro Médico', capacity: '80L', type: 'THERAPEUTIC', active: true, createdAt: '', updatedAt: '' }
        ]);
      } finally {
        setLoadingPools(false);
      }
    };

    const loadTechnicians = async () => {
      try {
        setLoadingTechnicians(true);
        setTechniciansError('');
        console.log('Cargando técnicos desde API...');
        const techniciansData = await technicianApi.getAll();
        console.log('Técnicos cargados:', techniciansData);
        setTechnicians(techniciansData.filter(tech => tech.active));
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        console.error('Error al cargar técnicos:', error);
        setTechniciansError(`Error de conexión: ${errorMessage}`);

        // Usar datos mock si falla la API
        setTechnicians([
          { id: 1, name: 'María García', email: 'maria@poolchem.com', specialty: 'Análisis Químico', active: true },
          { id: 2, name: 'Carlos López', email: 'carlos@poolchem.com', specialty: 'Mantenimiento', active: true },
          { id: 3, name: 'Ana Martín', email: 'ana@poolchem.com', specialty: 'Supervisión', active: true }
        ]);
      } finally {
        setLoadingTechnicians(false);
      }
    };

    const loadParameters = async () => {
      try {
        setLoadingParameters(true);
        console.log('Cargando parámetros desde API...');
        const parametersData = await parameterApi.getAll();
        console.log('Parámetros cargados:', parametersData);
        setParameters(parametersData.filter(param => param.active));
      } catch (error) {
        console.error('Error al cargar parámetros:', error);
        // Fallback vacío - los rangos no se mostrarán si falla
        setParameters([]);
      } finally {
        setLoadingParameters(false);
      }
    };

    loadPools();
    loadTechnicians();
    loadParameters();
  }, []);

  // Cuando cambia la piscina, carga el inventario de esa piscina
  useEffect(() => {
    if (!formData.poolId) {
      setPoolInventory([]);
      return;
    }
    const loadPoolInventory = async () => {
      setLoadingInventory(true);
      try {
        // Intentar cargar inventario específico de la piscina
        const res = await inventoryApi.getByPool(parseInt(formData.poolId), 0, 100);
        const poolItems = res.content.filter(inv => inv.active && inv.current_stock > 0);

        if (poolItems.length > 0) {
          setPoolInventory(poolItems);
        } else {
          // Si la piscina no tiene inventario propio, cargar todo el del tenant
          console.log('Pool sin inventario propio, cargando inventario del tenant...');
          const allRes = await inventoryApi.getAll(0, 100);
          setPoolInventory(allRes.content.filter(inv => inv.active && inv.current_stock > 0));
        }
      } catch {
        // Si falla el endpoint por pool, cargar todo el inventario del tenant
        try {
          const res = await inventoryApi.getAll(0, 100);
          setPoolInventory(res.content.filter(inv => inv.active && inv.current_stock > 0));
        } catch {
          setPoolInventory([]);
        }
      } finally {
        setLoadingInventory(false);
      }
    };
    loadPoolInventory();
  }, [formData.poolId]);

  // Helper para obtener un parámetro del backend por su ID de formulario
  const getParameterByFormId = useCallback((formId: string): PoolParameter | undefined => {
    const backendId = PARAM_ID_MAPPING[formId] || formId;
    return parameters.find(p => p.parameterId === backendId);
  }, [parameters]);

  // Helper para obtener el rango óptimo de un parámetro con fallbacks específicos
  const getParameterRange = useCallback((formId: string): string => {
    const param = getParameterByFormId(formId);

    // Fallbacks específicos para salt y salinity
    const FALLBACK_RANGES: { [key: string]: string } = {
      'salt': '4-5 ppm',           // Cloro Salino
      'salinity': '2700-3400 ppm'  // Salinidad Total
    };

    if (!param) {
      return FALLBACK_RANGES[formId] || '';
    }

    // Validar que el rango sea coherente con el tipo de parámetro
    // salt (cloro salino) debe tener rangos bajos (< 100)
    // salinity (salinidad total) debe tener rangos altos (> 1000)
    if (formId === 'salt' && param.optimalMax > 100) {
      // El backend tiene datos incorrectos para salt, usar fallback
      return FALLBACK_RANGES['salt'];
    }
    if (formId === 'salinity' && param.optimalMax < 1000) {
      // El backend tiene datos incorrectos para salinity, usar fallback
      return FALLBACK_RANGES['salinity'];
    }

    return `${param.optimalMin}-${param.optimalMax} ${param.unit}`;
  }, [getParameterByFormId]);

  const validateParameter = useCallback((paramId: string, value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';

    // Buscar el parámetro en los datos del backend
    const param = getParameterByFormId(paramId);

    if (!param) return '';

    if (numValue >= param.optimalMin && numValue <= param.optimalMax) {
      return 'optimal';
    } else if (numValue >= param.warningMin && numValue <= param.warningMax) {
      return 'warning';
    } else {
      return 'critical';
    }
  }, [getParameterByFormId]);

  const handleInputChange = (field: keyof Omit<FormData, 'chemicalUsages'>, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validate parameter if it's a chemical parameter
    const parameterMap: {[key: string]: string} = {
      alkalinity: 'alkalinity',
      ph: 'ph',
      stabilizer: 'stabilizer',
      freeChlorine: 'freeChlorine',
      combinedChlorine: 'combinedChlorine',
      hardness: 'hardness',
      phosphates: 'phosphates',
      salt: 'salt',
      salinity: 'salinity',
      scuba_alkalinity: 'scuba_alkalinity',
      scuba_bromine: 'scuba_bromine',
      scuba_chlorine: 'scuba_chlorine',
      scuba_copper: 'scuba_copper',
      scuba_cya: 'scuba_cya',
      scuba_hardness: 'scuba_hardness',
      scuba_phosphate: 'scuba_phosphate',
      scuba_oxygen: 'scuba_oxygen',
      scuba_ph: 'scuba_ph'
    };

    if (parameterMap[field]) {
      const status = validateParameter(parameterMap[field], value);
      setValidationStatus(prev => ({ ...prev, [field]: status }));
    }
  };

  // Maneja cambio en cantidad usada de un inventario específico
  const handleChemicalUsageChange = (inventoryId: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      chemicalUsages: { ...prev.chemicalUsages, [inventoryId]: value }
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, sampleImage: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const getInputBorderColor = (field: string) => {
    const status = validationStatus[field];
    switch (status) {
      case 'optimal': return 'border-green-500 bg-green-50';
      case 'warning': return 'border-yellow-500 bg-yellow-50';
      case 'critical': return 'border-red-500 bg-red-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getValidationIcon = (field: string) => {
    const status = validationStatus[field];
    switch (status) {
      case 'optimal': return 'ri-check-line text-green-500';
      case 'warning': return 'ri-alert-line text-yellow-500';
      case 'critical': return 'ri-close-line text-red-500';
      default: return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.poolId) {
      alert('Por favor selecciona una piscina antes de continuar');
      return;
    }

    try {
      const measurementData = convertFormDataToMeasurement();
      console.log('Enviando medición:', measurementData);
      const savedMeasurement = await measurementApi.create(measurementData);
      console.log('Medición guardada:', savedMeasurement);

      // Si hay químicos usados, registrarlos en el inventario
      const chemicalUsageItems = Object.entries(formData.chemicalUsages)
        .filter(([, qty]) => parseFloat(qty) > 0)
        .map(([invId, qty]) => {
          const inv = poolInventory.find(i => i.id === parseInt(invId));
          return {
            chemical_product_id: inv!.chemical_product.id,
            quantity_used: parseFloat(qty),
            notes: `Medición ${savedMeasurement.id}`,
          };
        });

      if (chemicalUsageItems.length > 0 && formData.technician) {
        setSubmittingUsage(true);
        const usageRequest: ChemicalUsageRequest = {
          pool_id: parseInt(formData.poolId),
          technician_id: parseInt(formData.technician),
          measurement_reference_id: savedMeasurement.id,
          notes: formData.notes || 'Uso registrado desde formulario de medición',
          chemical_usages: chemicalUsageItems,
        };
        try {
          const usageResult = await inventoryApi.registerUsage(usageRequest);
          const warnings = usageResult.affected_inventories.filter(a => a.low_stock_warning);
          const warningMsg = warnings.length > 0
            ? `\n⚠️ Stock bajo en: ${warnings.map(w => w.product_name).join(', ')}`
            : '';
          alert(`✅ Registro guardado.\nMedición ID: ${savedMeasurement.id}\nQuímicos registrados: ${usageResult.movements_created}${warningMsg}`);
        } catch (usageErr) {
          const msg = usageErr instanceof Error ? usageErr.message : 'Error desconocido';
          alert(`✅ Medición guardada (ID: ${savedMeasurement.id})\n⚠️ Error al registrar uso de químicos: ${msg}`);
        } finally {
          setSubmittingUsage(false);
        }
      } else {
        alert(`✅ Registro guardado exitosamente.\nID: ${savedMeasurement.id}`);
      }
    } catch (error) {
      console.error('Error al guardar medición:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      alert(`Error al guardar: ${errorMessage}`);
    }
  };

  const getSelectedPool = () => {
    return pools.find(pool => pool.id.toString() === formData.poolId);
  };

  // Helper function para determinar el status de una lectura
  const determineReadingStatus = (paramId: string, value: number): ReadingStatus => {
    const status = validateParameter(paramId, value.toString());
    switch (status) {
      case 'optimal': return 'OPTIMAL';
      case 'warning': return 'WARNING';
      case 'critical': return 'CRITICAL';
      default: return 'OPTIMAL';
    }
  };

  // Helper function para convertir formData a MeasurementCreateRequest
  const convertFormDataToMeasurement = (): MeasurementCreateRequest => {
    const readings: Reading[] = [];

    // Mapeo de parámetros estándar
    const standardParams = [
      { id: 'alkalinity', value: formData.alkalinity, unit: 'ppm' },
      { id: 'ph', value: formData.ph, unit: 'pH' },
      { id: 'stabilizer', value: formData.stabilizer, unit: 'ppm' },
      { id: 'freeChlorine', value: formData.freeChlorine, unit: 'ppm' },
      { id: 'combinedChlorine', value: formData.combinedChlorine, unit: 'ppm' },
      { id: 'hardness', value: formData.hardness, unit: 'ppm' },
      { id: 'phosphates', value: formData.phosphates, unit: 'ppb' },
      { id: 'salt', value: formData.salt, unit: 'ppm' },           // Cloro Salino (4-5 ppm)
      { id: 'salinity', value: formData.salinity, unit: 'ppm' }    // Salinidad Total (2700-3400 ppm)
    ];

    // Mapeo de parámetros Scuba3s
    const scubaParams = [
      { id: 'scuba_alkalinity', value: formData.scuba_alkalinity, unit: 'mg/L' },
      { id: 'scuba_bromine', value: formData.scuba_bromine, unit: 'mg/L' },
      { id: 'scuba_chlorine', value: formData.scuba_chlorine, unit: 'mg/L' },
      { id: 'scuba_copper', value: formData.scuba_copper, unit: 'mg/L' },
      { id: 'scuba_cya', value: formData.scuba_cya, unit: 'mg/L' },
      { id: 'scuba_hardness', value: formData.scuba_hardness, unit: 'mg/L' },
      { id: 'scuba_phosphate', value: formData.scuba_phosphate, unit: 'mg/L' },
      { id: 'scuba_oxygen', value: formData.scuba_oxygen, unit: 'mg/L' },
      { id: 'scuba_ph', value: formData.scuba_ph, unit: 'pH' }
    ];

    // Agregar lecturas estándar
    if (formData.measurementDevice === 'standard' || formData.measurementDevice === 'both') {
      standardParams.forEach(param => {
        if (param.value && param.value.trim() !== '') {
          const value = parseFloat(param.value);
          if (!isNaN(value)) {
            readings.push({
              parameterId: param.id,
              value: value,
              unit: param.unit,
              status: determineReadingStatus(param.id, value)
            });
          }
        }
      });
    }

    // Agregar lecturas Scuba3s
    if (formData.measurementDevice === 'scuba3s' || formData.measurementDevice === 'both') {
      scubaParams.forEach(param => {
        if (param.value && param.value.trim() !== '') {
          const value = parseFloat(param.value);
          if (!isNaN(value)) {
            readings.push({
              parameterId: param.id,
              value: value,
              unit: param.unit,
              status: determineReadingStatus(param.id, value)
            });
          }
        }
      });
    }

    return {
      poolId: parseInt(formData.poolId),
      technicianId: formData.technician ? parseInt(formData.technician) : undefined,
      source: 'MANUAL',
      readings: readings,
      timestamp: new Date(`${formData.date}T${formData.time}:00`).toISOString(),
      notes: formData.notes || undefined
    };
  };

  return (
    <section id="registro" className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">Registro de Datos</h2>
          <p className="text-xl text-slate-600">Captura nueva medición de parámetros químicos</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Formulario Principal */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <i className="ri-clipboard-line text-3xl text-teal-600 mr-3"></i>
                  <h3 className="text-2xl font-bold text-slate-800">Nueva Medición</h3>
                </div>
                <div>
                  <select
                    value={formData.measurementDevice}
                    onChange={(e) => handleInputChange('measurementDevice', e.target.value as 'standard' | 'scuba3s' | 'both')}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer text-sm"
                  >
                    <option value="both">Todos los dispositivos</option>
                    <option value="standard">Medición Estándar</option>
                    <option value="scuba3s">Dispositivo Scuba3s</option>
                  </select>
                </div>
              </div>

              {/* Selección de Piscina */}
              <div className="mb-8 p-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border-2 border-teal-100">
                <div className="flex items-center mb-4">
                  <i className="ri-building-2-line text-2xl text-teal-600 mr-3"></i>
                  <h4 className="text-lg font-bold text-slate-800">Selección de Piscina</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Piscina <span className="text-red-500">*</span>
                    </label>
                    {loadingPools ? (
                      <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                        <i className="ri-loader-line animate-spin mr-2 text-teal-600"></i>
                        <span className="text-gray-500">Cargando piscinas...</span>
                      </div>
                    ) : poolsError ? (
                      <div className="w-full px-4 py-3 border border-orange-300 rounded-lg bg-orange-50">
                        <div className="flex items-center text-orange-700 text-sm mb-2">
                          <i className="ri-alert-line mr-2"></i>
                          <span className="font-medium">Usando datos locales</span>
                        </div>
                        <div className="text-xs text-orange-600">{poolsError}</div>
                        <div className="mt-2">
                          <select
                            value={formData.poolId}
                            onChange={(e) => handleInputChange('poolId', e.target.value)}
                            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer transition-all ${
                              formData.poolId ? 'border-teal-300 bg-white' : 'border-red-300 bg-red-50'
                            }`}
                            required
                          >
                            <option value="">Seleccionar piscina</option>
                            {pools.map((pool) => (
                              <option key={pool.id} value={pool.id.toString()}>
                                {pool.name} - {pool.location}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ) : (
                      <select
                        value={formData.poolId}
                        onChange={(e) => handleInputChange('poolId', e.target.value)}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer transition-all ${
                          formData.poolId ? 'border-teal-300 bg-white' : 'border-red-300 bg-red-50'
                        }`}
                        required
                      >
                        <option value="">Seleccionar piscina</option>
                        {pools.map((pool) => (
                          <option key={pool.id} value={pool.id.toString()}>
                            {pool.name} - {pool.location}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {getSelectedPool() && (
                    <div className="bg-white p-4 rounded-lg border border-teal-200">
                      <h5 className="text-sm font-semibold text-slate-700 mb-2">Detalles de la Piscina</h5>
                      <div className="space-y-1 text-xs text-slate-600">
                        <div><span className="font-medium">Tipo:</span> {getSelectedPool()?.type}</div>
                        <div><span className="font-medium">Capacidad:</span> {getSelectedPool()?.capacity}</div>
                        <div><span className="font-medium">Ubicación:</span> {getSelectedPool()?.location}</div>
                      </div>
                    </div>
                  )}
                </div>

                {!formData.poolId && (
                  <div className="mt-3 flex items-center text-red-600 text-sm">
                    <i className="ri-error-warning-line mr-1"></i>
                    <span>Debes seleccionar una piscina para continuar con el registro</span>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Parámetros Estándar */}
                {(formData.measurementDevice === 'standard' || formData.measurementDevice === 'both') && (
                  <div className="mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-test-tube-line text-xl text-teal-600"></i>
                      </div>
                      <h4 className="text-lg font-bold text-slate-800">Medición Estándar</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Alcalinidad */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 80-120 ppm
✅ Ideal: 100 ppm
⚠️ Bajo: < 80 | Alto: > 120">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Alcalinidad {getParameterRange('alkalinity') && `(${getParameterRange('alkalinity')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.alkalinity}
                            onChange={(e) => handleInputChange('alkalinity', e.target.value)}
                            placeholder="Ej: 100"
                            className={`w-full pl-4 pr-20 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${getInputBorderColor('alkalinity')}`}
                          />
                          <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ppm</span>
                          {getValidationIcon('alkalinity') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('alkalinity')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* pH */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 7.2 - 7.6
✅ Ideal: 7.4
⚠️ Ácido: < 7.2 | Alcalino: > 7.6">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          pH {getParameterRange('ph') && `(${getParameterRange('ph')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.ph}
                            onChange={(e) => handleInputChange('ph', e.target.value)}
                            className={`w-full pl-4 pr-10 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${getInputBorderColor('ph')}`}
                            placeholder="Ej: 7.4"
                          />
                          {getValidationIcon('ph') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('ph')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Estabilizante */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 30-60 ppm
✅ Ideal: 40 ppm
⚠️ Bajo: < 30 | Alto: > 80 ppm (sobredosificación)">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Estabilizante {getParameterRange('stabilizer') && `(${getParameterRange('stabilizer')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.stabilizer}
                            onChange={(e) => handleInputChange('stabilizer', e.target.value)}
                            placeholder="Ej: 40"
                            className={`w-full pl-4 pr-20 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${getInputBorderColor('stabilizer')}`}
                          />
                          <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ppm</span>
                          {getValidationIcon('stabilizer') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('stabilizer')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Cloro Libre */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 1.0-3.0 ppm
✅ Ideal: 2.0 ppm
⚠️ Bajo: < 1.0 | Alto: > 5.0 ppm">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Cloro Libre {getParameterRange('freeChlorine') && `(${getParameterRange('freeChlorine')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.freeChlorine}
                            onChange={(e) => handleInputChange('freeChlorine', e.target.value)}
                            placeholder="Ej: 2.0"
                            className={`w-full pl-4 pr-20 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${getInputBorderColor('freeChlorine')}`}
                          />
                          <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ppm</span>
                          {getValidationIcon('freeChlorine') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('freeChlorine')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Cloro Combinado */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Máximo permitido: 0.5 ppm
✅ Ideal: < 0.2 ppm
⚠️ Alto: > 0.5 ppm (irritación)">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Cloro Combinado {getParameterRange('combinedChlorine') && `(${getParameterRange('combinedChlorine')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.combinedChlorine}
                            onChange={(e) => handleInputChange('combinedChlorine', e.target.value)}
                            placeholder="Ej: 0.2"
                            className={`w-full pl-4 pr-20 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${getInputBorderColor('combinedChlorine')}`}
                          />
                          <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ppm</span>
                          {getValidationIcon('combinedChlorine') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('combinedChlorine')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Dureza */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 200-400 ppm
✅ Ideal: 250 ppm
⚠️ Blanda: < 150 | Muy dura: > 500 ppm">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Dureza {getParameterRange('hardness') && `(${getParameterRange('hardness')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.hardness}
                            onChange={(e) => handleInputChange('hardness', e.target.value)}
                            placeholder="Ej: 250"
                            className={`w-full pl-4 pr-20 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${getInputBorderColor('hardness')}`}
                          />
                          <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ppm</span>
                          {getValidationIcon('hardness') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('hardness')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Fosfatos */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Máximo recomendado: 200 ppb
✅ Ideal: < 100 ppb
⚠️ Alto: > 200 ppb (favorece algas)">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Fosfatos {getParameterRange('phosphates') && `(${getParameterRange('phosphates')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.phosphates}
                            onChange={(e) => handleInputChange('phosphates', e.target.value)}
                            placeholder="Ej: 125"
                            className={`w-full pl-4 pr-20 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${getInputBorderColor('phosphates')}`}
                          />
                          <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ppb</span>
                          {getValidationIcon('phosphates') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('phosphates')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Cloro Salino (Cloro producido por sal) - ID: salt, Rango: 4-5 ppm */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 4.0-5.0 ppm
✅ Ideal: 4.5 ppm
🔵 Cloro generado por cloración salina">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          <i className="ri-drop-line text-teal-500 mr-1"></i>
                          Cloro Salino (4-5 ppm)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.salt}
                            onChange={(e) => handleInputChange('salt', e.target.value)}
                            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${getInputBorderColor('salt')}`}
                            placeholder="Ej: 4.5"
                          />
                          <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ppm</span>
                          {getValidationIcon('salt') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('salt')}`}></i>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Cloro producido por cloración salina</p>
                      </div>

                      {/* Salinidad Total (Concentración física de sal) */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 2700-3400 ppm
✅ Ideal: 3000 ppm
🔵 Lectura del equipo clorador de sal">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          <i className="ri-contrast-drop-line text-blue-500 mr-1"></i>
                          Salinidad Total ({getParameterRange('salinity') || '2700-3400 ppm'})
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="1"
                            value={formData.salinity}
                            onChange={(e) => handleInputChange('salinity', e.target.value)}
                            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${getInputBorderColor('salinity')}`}
                            placeholder="Ej: 3000"
                          />
                          <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ppm</span>
                          {getValidationIcon('salinity') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('salinity')}`}></i>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Lectura del equipo clorador</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Parámetros Scuba3s */}
                {(formData.measurementDevice === 'scuba3s' || formData.measurementDevice === 'both') && (
                  <div className="mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-device-line text-xl text-indigo-600"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-slate-800">Dispositivo Scuba3s</h4>
                        <p className="text-xs text-slate-500">Variables de medición digital</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* Alcalinidad-m T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 80-120 mg/L
✅ Ideal: 100 mg/L
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Alcalinidad-m T {getParameterRange('scuba_alkalinity') && `(${getParameterRange('scuba_alkalinity')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.scuba_alkalinity}
                            onChange={(e) => handleInputChange('scuba_alkalinity', e.target.value)}
                            placeholder="Ej: 100"
                            className={`w-full pl-4 pr-20 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_alkalinity')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">mg/L</span>
                          {getValidationIcon('scuba_alkalinity') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_alkalinity')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Bromo T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 2.0-4.0 mg/L Br2
✅ Ideal: 3.0 mg/L
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Bromo T {getParameterRange('scuba_bromine') && `(${getParameterRange('scuba_bromine')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.scuba_bromine}
                            onChange={(e) => handleInputChange('scuba_bromine', e.target.value)}
                            placeholder="Ej: 3.0"
                            className={`w-full pl-4 pr-24 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_bromine')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">mg/L Br2</span>
                          {getValidationIcon('scuba_bromine') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_bromine')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Cloro T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 1.0-3.0 mg/L Cl2
✅ Ideal: 2.0 mg/L
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Cloro T {getParameterRange('scuba_chlorine') && `(${getParameterRange('scuba_chlorine')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.scuba_chlorine}
                            onChange={(e) => handleInputChange('scuba_chlorine', e.target.value)}
                            placeholder="Ej: 2.0"
                            className={`w-full pl-4 pr-24 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_chlorine')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">mg/L Cl2</span>
                          {getValidationIcon('scuba_chlorine') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_chlorine')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Cobre T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Máximo: 1.0 mg/L Cu
✅ Ideal: < 0.1 mg/L
⚠️ Alto: > 1.0 (mancha superficies)
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Cobre T {getParameterRange('scuba_copper') && `(${getParameterRange('scuba_copper')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.scuba_copper}
                            onChange={(e) => handleInputChange('scuba_copper', e.target.value)}
                            placeholder="Ej: 0.1"
                            className={`w-full pl-4 pr-24 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_copper')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">mg/L Cu</span>
                          {getValidationIcon('scuba_copper') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_copper')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* CyA T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 30-60 mg/L CyA
✅ Ideal: 40 mg/L
⚠️ Alto: > 100 mg/L (reduce efectividad del cloro)
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          CyA T {getParameterRange('scuba_cya') && `(${getParameterRange('scuba_cya')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.scuba_cya}
                            onChange={(e) => handleInputChange('scuba_cya', e.target.value)}
                            placeholder="Ej: 40"
                            className={`w-full pl-4 pr-28 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_cya')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">mg/L CyA</span>
                          {getValidationIcon('scuba_cya') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_cya')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Dureza Calcium (B) T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 200-400 mg/L
✅ Ideal: 250 mg/L
⚠️ Blanda: < 150 | Muy dura: > 500
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Dureza Calcium {getParameterRange('scuba_hardness') && `(${getParameterRange('scuba_hardness')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.scuba_hardness}
                            onChange={(e) => handleInputChange('scuba_hardness', e.target.value)}
                            placeholder="Ej: 250"
                            className={`w-full pl-4 pr-20 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_hardness')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">mg/L</span>
                          {getValidationIcon('scuba_hardness') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_hardness')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Fosfato LR T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Máximo: 0.10 mg/L PO4
✅ Ideal: < 0.05 mg/L
⚠️ Alto: favorece crecimiento de algas
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Fosfato LR T {getParameterRange('scuba_phosphate') && `(${getParameterRange('scuba_phosphate')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            value={formData.scuba_phosphate}
                            onChange={(e) => handleInputChange('scuba_phosphate', e.target.value)}
                            placeholder="Ej: 0.05"
                            className={`w-full pl-4 pr-28 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_phosphate')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">mg/L PO4</span>
                          {getValidationIcon('scuba_phosphate') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_phosphate')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* Oxígeno Activo T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 4.0-8.0 mg/L O2
✅ Ideal: 5.0 mg/L
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Oxígeno Activo T {getParameterRange('scuba_oxygen') && `(${getParameterRange('scuba_oxygen')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.scuba_oxygen}
                            onChange={(e) => handleInputChange('scuba_oxygen', e.target.value)}
                            placeholder="Ej: 5.0"
                            className={`w-full pl-4 pr-24 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_oxygen')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">mg/L O2</span>
                          {getValidationIcon('scuba_oxygen') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_oxygen')}`}></i>
                          )}
                        </div>
                      </div>

                      {/* pH T */}
                      <div className="relative pool-tooltip" data-tooltip="📊 Rango óptimo: 7.2 - 7.6
✅ Ideal: 7.4
⚠️ Ácido: < 7.2 | Alcalino: > 7.6
🔬 Medición Scuba3s">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          pH T {getParameterRange('scuba_ph') && `(${getParameterRange('scuba_ph')})`}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            value={formData.scuba_ph}
                            onChange={(e) => handleInputChange('scuba_ph', e.target.value)}
                            placeholder="Ej: 7.4"
                            className={`w-full pl-4 pr-16 py-3 border-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${getInputBorderColor('scuba_ph')}`}
                          />
                          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">pH</span>
                          {getValidationIcon('scuba_ph') && (
                            <i className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${getValidationIcon('scuba_ph')}`}></i>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Productos Químicos Utilizados — Dinámico desde inventario del tenant */}
                <div className="border-t pt-6 mt-8">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-flask-fill text-xl text-amber-600"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-slate-800">Productos Químicos Utilizados</h4>
                      <p className="text-xs text-slate-500">
                        {formData.poolId
                          ? 'Ingrese las cantidades aplicadas en esta sesión'
                          : 'Selecciona una piscina para ver el inventario disponible'}
                      </p>
                    </div>
                  </div>

                  {!formData.poolId ? (
                    <div className="flex items-center p-4 bg-amber-50 rounded-xl border border-amber-200 text-amber-700">
                      <i className="ri-information-line text-lg mr-2"></i>
                      <span className="text-sm">Selecciona una piscina para cargar su inventario de químicos.</span>
                    </div>
                  ) : loadingInventory ? (
                    <div className="flex items-center justify-center py-6">
                      <i className="ri-loader-line animate-spin text-2xl text-amber-600 mr-2"></i>
                      <span className="text-slate-500">Cargando inventario...</span>
                    </div>
                  ) : poolInventory.length === 0 ? (
                    <div className="flex items-center p-4 bg-gray-50 rounded-xl border border-gray-200 text-gray-500">
                      <i className="ri-error-warning-line text-lg mr-2"></i>
                      <span className="text-sm">No hay productos con stock disponible para esta piscina.</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {poolInventory.map((inv) => {
                        const qty = formData.chemicalUsages[inv.id] || '';
                        const isLow = inv.stock_status === 'LOW_STOCK';
                        const isOut = inv.stock_status === 'OUT_OF_STOCK';
                        return (
                          <div key={inv.id} className={`relative p-4 rounded-xl border-2 transition-all ${
                            isOut ? 'border-red-200 bg-red-50' :
                            isLow ? 'border-amber-200 bg-amber-50' :
                            'border-gray-200 bg-white hover:border-teal-300'
                          }`}>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">
                              {inv.chemical_product.name}
                              {isLow && <span className="ml-2 text-xs text-amber-600 font-normal">⚠ stock bajo</span>}
                              {isOut && <span className="ml-2 text-xs text-red-600 font-normal">✗ sin stock</span>}
                            </label>
                            <p className="text-xs text-slate-400 mb-2">
                              Disponible: <strong>{inv.current_stock.toFixed(2)} {inv.chemical_product.unit}</strong>
                              {inv.pool && <span className="ml-1 text-slate-300">· {inv.pool.name}</span>}
                            </p>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.001"
                                min="0"
                                max={inv.current_stock}
                                value={qty}
                                onChange={(e) => handleChemicalUsageChange(inv.id, e.target.value)}
                                disabled={isOut}
                                className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-sm ${
                                  isOut ? 'opacity-40 cursor-not-allowed bg-gray-100' : 'bg-white'
                                } ${parseFloat(qty) > inv.current_stock ? 'border-red-400' : 'border-gray-300'}`}
                                placeholder="0.000"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">
                                {inv.chemical_product.unit}
                              </span>
                            </div>
                            {parseFloat(qty) > inv.current_stock && (
                              <p className="text-xs text-red-500 mt-1">⚠ Supera el stock disponible</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Metadatos */}
                <div className="border-t pt-6 mt-8">
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">Información Adicional</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Fecha</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => handleInputChange('date', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Hora</label>
                      <input
                        type="time"
                        value={formData.time}
                        onChange={(e) => handleInputChange('time', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Técnico Responsable</label>
                    {loadingTechnicians ? (
                      <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                        <i className="ri-loader-line animate-spin mr-2 text-teal-600"></i>
                        <span className="text-gray-500">Cargando técnicos...</span>
                      </div>
                    ) : techniciansError ? (
                      <div className="w-full px-4 py-3 border border-orange-300 rounded-lg bg-orange-50">
                        <div className="flex items-center text-orange-700 text-sm mb-2">
                          <i className="ri-alert-line mr-2"></i>
                          <span className="font-medium">Usando datos locales</span>
                        </div>
                        <div className="text-xs text-orange-600 mb-2">{techniciansError}</div>
                        <select
                          value={formData.technician}
                          onChange={(e) => handleInputChange('technician', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                        >
                          <option value="">Seleccionar técnico</option>
                          {technicians.map((technician) => (
                            <option key={technician.id} value={technician.id.toString()}>
                              {technician.name} - {technician.specialty}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <select
                        value={formData.technician}
                        onChange={(e) => handleInputChange('technician', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                      >
                        <option value="">Seleccionar técnico</option>
                        {technicians.map((technician) => (
                          <option key={technician.id} value={technician.id.toString()}>
                            {technician.name} - {technician.specialty}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Notas y Observaciones</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 resize-none"
                      placeholder="Observaciones adicionales (opcional)"
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <i className="ri-camera-line mr-2 text-teal-600"></i>
                      Foto de la Muestra
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                    />
                    {formData.sampleImage && (
                      <div className="mt-4 w-full h-64 rounded-lg overflow-hidden border-2 border-teal-200">
                        <img src={formData.sampleImage} alt="Muestra" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-2">
                      Sube una foto de la toma de muestra para referencia visual
                    </p>
                  </div>
                </div>

                {/* Botón de Envío */}
                <button
                  type="submit"
                  disabled={submittingUsage}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:from-teal-600 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {submittingUsage ? (
                    <><i className="ri-loader-line animate-spin mr-2"></i>Registrando uso de químicos...</>
                  ) : (
                    <><i className="ri-save-line mr-2"></i>Guardar Registro</>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Panel de Asistente */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-teal-50 to-cyan-100 rounded-3xl p-8 mb-6">
              <div className="flex items-center mb-6">
                <i className="ri-guide-line text-3xl text-teal-600 mr-3"></i>
                <h3 className="text-xl font-bold text-slate-800">Guía de Medición</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">1</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Calibrar Equipos</p>
                    <p className="text-xs text-slate-600">Verificar calibración de medidores antes de usar</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">2</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Tomar Muestras</p>
                    <p className="text-xs text-slate-600">Recolectar agua a 30cm de profundidad</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">3</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Medir Parámetros</p>
                    <p className="text-xs text-slate-600">Seguir secuencia: pH, cloro, alcalinidad</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-1">4</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Registrar Datos</p>
                    <p className="text-xs text-slate-600">Introducir valores inmediatamente</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-white/50 rounded-lg">
                <p className="text-sm font-semibold text-slate-700 mb-2">Frecuencia Recomendada</p>
                <p className="text-xs text-slate-600">📅 Medición diaria durante temporada alta</p>
                <p className="text-xs text-slate-600">📅 3 veces por semana en temporada baja</p>
              </div>
            </div>

            {/* Rangos Scuba3s - Dinámicos desde el backend */}
            <div className="bg-gradient-to-br from-indigo-50 to-violet-100 rounded-3xl p-6 mb-6">
              <div className="flex items-center mb-4">
                <i className="ri-device-line text-2xl text-indigo-600 mr-2"></i>
                <h3 className="text-lg font-bold text-slate-800">Rangos Scuba3s</h3>
              </div>
              <div className="space-y-2 text-xs">
                {loadingParameters ? (
                  <div className="flex items-center justify-center py-4">
                    <i className="ri-loader-line animate-spin mr-2 text-indigo-600"></i>
                    <span className="text-slate-500">Cargando rangos...</span>
                  </div>
                ) : (
                  <>
                    {parameters
                      .filter(p => p.parameterId.startsWith('scuba_'))
                      .map(param => (
                        <div key={param.id} className="flex justify-between p-2 bg-white/50 rounded">
                          <span className="text-slate-600">{param.name}</span>
                          <span className="font-semibold text-indigo-700">
                            {param.optimalMin}-{param.optimalMax} {param.unit}
                          </span>
                        </div>
                      ))
                    }
                    {parameters.filter(p => p.parameterId.startsWith('scuba_')).length === 0 && (
                      <div className="text-center py-4 text-slate-500">
                        No hay rangos disponibles
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Últimas Mediciones */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h4 className="text-lg font-semibold text-slate-800 mb-4">Últimas Mediciones</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">14 Enero - 08:00</p>
                    <p className="text-xs text-slate-500">pH: 7.4, Cl: 1.6 ppm</p>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">13 Enero - 08:00</p>
                    <p className="text-xs text-slate-500">pH: 7.3, Cl: 1.9 ppm</p>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">12 Enero - 08:00</p>
                    <p className="text-xs text-slate-500">pH: 7.1, Cl: 1.4 ppm</p>
                  </div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
